// =============================================================================
// src/data/sentinel-overlay.ts — Mapeia dashboard Sentinel → datasets do mockup.
// =============================================================================

import type { SentinelDashboard } from './sentinel-client';
import type {
  CopilotChatDataset,
  CopilotFeedItem,
  CopilotPlanAction,
  FeedSentiment,
} from '../features/copilot-chat/copilot-dataset';
import type { TacticPriority, ViewTacticCard } from '../chrome/copilot-sidebar/tactic-cards-by-section';
import { reputationBand } from '../features/reputation/reputation-data';

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}h${String(d.getMinutes()).padStart(2, '0')}`;
    /* v8 ignore next 3 -- `new Date(iso)` nunca lança (string inválida vira Invalid Date; getHours()/getMinutes() → NaN, String(NaN)='NaN', padStart ok); catch é guarda defensiva comprovadamente inalcançável (CLAUDE.md rule 11) */
  } catch {
    return '--:--';
  }
}

function mapSentClass(s: string): FeedSentiment {
  if (s === 'pos') return 'pos';
  if (s === 'neg') return 'neg';
  return 'neu';
}

function mapActions(actions: readonly Record<string, unknown>[]): CopilotPlanAction[] {
  return actions.map(
    (action): CopilotPlanAction => ({
      when: String(action.when ?? 'Imediato'),
      text: String(action.text ?? ''),
      tags: Array.isArray(action.tags) ? action.tags.map(String) : [],
    }),
  );
}

function liveRecommendationActions(dashboard: SentinelDashboard): CopilotPlanAction[] {
  const rows = dashboard.actionRecommendations ?? [];
  const preferred = rows.find((row) => row.surface_id === 'monitoramento' || row.action_type === 'crisis') ?? rows[0];
  return preferred ? mapActions(preferred.recommended_actions ?? []) : [];
}

const MONTHS_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Rótulo curto da série histórica: 'dd mmm'; o último ponto vira 'agora'. */
function formatTrendLabel(iso: string, isLast: boolean): string {
  if (isLast) return 'agora';
  const d = new Date(iso);
  const month = MONTHS_PT[d.getUTCMonth()];
  if (Number.isNaN(d.getTime()) || !month) return '';
  return `${d.getUTCDate()} ${month}`;
}

/** Versão mutável (remove readonly) para montar o overlay incrementalmente. */
type CopilotOverlayDraft = { -readonly [K in keyof CopilotChatDataset]?: CopilotChatDataset[K] };

export function buildCopilotOverlay(dashboard: SentinelDashboard): Partial<CopilotChatDataset> {
  const overlay: CopilotOverlayDraft = {};

  if (dashboard.sentiment) {
    const s = dashboard.sentiment;
    overlay.sentimento = {
      neg: Number(s.neg_pct ?? 0),
      neu: Number(s.neu_pct ?? 0),
      pos: Number(s.pos_pct ?? 0),
      total: Number(s.total ?? 0),
      foot: `Score médio ${Number(s.avg_weighted_score ?? s.avg_score ?? 0).toFixed(2)} · dados ao vivo Sentinel`,
    };
  }

  if (dashboard.feed.length > 0) {
    overlay.feed = dashboard.feed.slice(0, 8).map(
      (row): CopilotFeedItem => ({
        time: formatTime(row.published_at),
        src: row.author_name ?? row.platform ?? 'Rede social',
        sent: mapSentClass(row.sent_class),
        threat: Math.min(5, Math.max(1, row.threat_level ?? 2)),
        text: `"${(row.body_preview ?? '').slice(0, 180)}"`,
        tags: [row.platform ?? 'social', row.content_kind ?? 'post'],
      }),
    );
  }

  const liveActions = liveRecommendationActions(dashboard);
  const recommendation = dashboard.actionRecommendations?.[0];
  if (liveActions.length > 0) {
    overlay.plano = liveActions;
    overlay.diagnostico = {
      contexto: recommendation?.title ?? 'Recomendações de ação',
      impacto: recommendation?.summary ?? 'Plano sugerido pela IA, pendente de aprovação humana.',
      leitura: `Risco: ${recommendation?.risk_level ?? 'medium'} · confiança ${recommendation?.confidence ?? 0}% · fonte: gold.ai_action_recommendation`,
    };
  } else {
    const alert = dashboard.alerts[0];
    if (alert) {
      overlay.plano = mapActions(alert.recommended_actions ?? []);
      if (alert.summary) {
        overlay.diagnostico = {
          contexto: alert.title,
          impacto: alert.summary,
          leitura: `Severidade: ${alert.severity} · fonte: Sentinel/AtalaIA`,
        };
      }
    }
  }

  if (dashboard.reputation) {
    const r = dashboard.reputation;
    // band derivada do índice (era 'Bom' hardcoded — bug: qualquer índice mostrava "Bom").
    // reputationBand é a fonte única de classificação de faixa (DRY com o gauge de reputação).
    const index = Number(r.reputation_index ?? 50);
    overlay.irm = {
      value: index,
      band: reputationBand(index).name,
      trend: Number(r.nrs ?? 0) - 50,
      subdims: [
        { label: 'Percepção', val: Number(r.nrs ?? 50) },
        { label: 'Presença', val: Number(r.sov ?? 50) },
        { label: 'Protagonismo', val: Number(r.bps ?? 50) },
      ],
    };
  }

  return overlay;
}

export interface ReputationLivePatch {
  /** Índice de reputação do cliente vivo (substitui o valor estático no gauge). */
  readonly index?: number;
  readonly mentions24h?: number;
  /** Série histórica do índice (substitui REPUTATION_TREND) — do mais antigo ao 'agora'. */
  readonly trend?: readonly number[];
  readonly trendLabels?: readonly string[];
}

export function buildReputationPatch(dashboard: SentinelDashboard): ReputationLivePatch {
  if (!dashboard.reputation) return {};
  const patch: {
    index: number;
    mentions24h: number;
    trend?: readonly number[];
    trendLabels?: readonly string[];
  } = {
    index: Number(dashboard.reputation.reputation_index),
    mentions24h: Number(dashboard.reputation.mentions_24h),
  };
  if (dashboard.trend.length > 0) {
    const last = dashboard.trend.length - 1;
    patch.trend = dashboard.trend.map((p) => Number(p.reputation_index));
    patch.trendLabels = dashboard.trend.map((p, i) => formatTrendLabel(p.ts, i === last));
  }
  return patch;
}

// (BriefingLivePatch/buildBriefingPatch REMOVIDOS na reforma 2026-07-28: o
// briefing passou a ler o social_listening via analytics-live-store e a
// manchete IA do gold.daily_briefing estava parada desde 2026-06-24.)

export function buildCrisisVolumeSeries(dashboard: SentinelDashboard): number[] | null {
  const total = dashboard.sentiment?.total;
  if (!total || total <= 0) return null;
  const base = Math.max(1, Math.round(total / 24));
  return Array.from({ length: 24 }, (_, i) => Math.round(base * (0.6 + (i / 24) * 0.8)));
}

/** Amplificador (perfil/portal) de um tópico de crise, p/ o painel "Amplificadores". */
export interface CrisisAmplifier {
  readonly actor: string;
  readonly kind: string;
  readonly mentions: number;
  readonly reach: number;
  readonly sharePct: number;
  readonly isPortalEstimate: boolean;
}

/** Origem do tópico ("como começou"). */
export interface CrisisOrigin {
  readonly outlet: string;
  readonly channel: string;
  readonly publishedReliable: boolean;
  readonly url: string | null;
  readonly title: string | null;
  readonly preview: string | null;
}

/** Patch ao vivo da tela de Crise (escopo do tópico de maior gauge). Campos ausentes = mock. */
export interface CrisisLivePatch {
  readonly series?: readonly number[];
  readonly gauge?: number;
  readonly mentions24h?: number;
  readonly mentionsDeltaPct?: number | null;
  readonly netSentiment?: number | null;
  readonly acceleration?: string;
  readonly sourcesUnavailable?: number;
  readonly amplifiers?: readonly CrisisAmplifier[];
  readonly origin?: CrisisOrigin | null;
  readonly currentPhase?: string;
  readonly crisisState?: string;
}

/** Série horária de menções (24 pts) do tópico, de v_content_volume_hourly (já ordenada asc). */
function crisisHourlySeries(rows: SentinelDashboard['volumeHourly'], topicId: string): number[] {
  return (rows ?? [])
    .filter((row) => row.topic_id === topicId)
    .map((row) => Number(row.mentions))
    .slice(-24);
}

function toCrisisAmplifier(row: NonNullable<SentinelDashboard['amplifiers']>[number]): CrisisAmplifier {
  return {
    actor: row.actor,
    kind: row.kind,
    mentions: Number(row.mentions),
    reach: Number(row.reach),
    sharePct: Number(row.share_pct ?? 0),
    isPortalEstimate: row.reach_is_portal_estimate,
  };
}

function toCrisisOrigin(row: NonNullable<SentinelDashboard['origin']>[number]): CrisisOrigin {
  return {
    outlet: row.outlet ?? '—',
    channel: row.channel_label ?? '',
    publishedReliable: row.published_reliable,
    url: row.url,
    title: row.title,
    preview: row.preview,
  };
}

/** Mapeia o dashboard → patch da Crise. Escopo: o tópico de MAIOR gauge (crisisMetrics[0]).
 *  Sem crise ativa (crisisMetrics vazio) → patch vazio (a tela mantém o mock). */
export function buildCrisisLivePatch(dashboard: SentinelDashboard): CrisisLivePatch {
  const metrics = dashboard.crisisMetrics?.[0] ?? null;
  if (!metrics) return {};
  const topicId = metrics.topic_id;
  const series = crisisHourlySeries(dashboard.volumeHourly, topicId);
  const origin = (dashboard.origin ?? []).find((row) => row.topic_id === topicId) ?? null;
  const lifecycle = (dashboard.lifecycle ?? []).find((row) => row.topic_id === topicId) ?? null;
  const patch: {
    series?: readonly number[];
    gauge: number;
    mentions24h: number;
    mentionsDeltaPct: number | null;
    netSentiment: number | null;
    acceleration: string;
    sourcesUnavailable: number;
    amplifiers: readonly CrisisAmplifier[];
    origin?: CrisisOrigin;
    currentPhase?: string;
    crisisState?: string;
  } = {
    gauge: Number(metrics.gauge_0_100),
    mentions24h: Number(metrics.mentions_24h),
    mentionsDeltaPct: metrics.mentions_delta_pct,
    netSentiment: metrics.net_sentiment,
    acceleration: metrics.acceleration,
    sourcesUnavailable: Number(metrics.sources_unavailable),
    amplifiers: (dashboard.amplifiers ?? []).filter((row) => row.topic_id === topicId).map(toCrisisAmplifier),
  };
  if (series.length) patch.series = series;
  if (origin) patch.origin = toCrisisOrigin(origin);
  if (lifecycle) {
    patch.currentPhase = lifecycle.current_phase;
    patch.crisisState = lifecycle.crisis_state;
  }
  return patch;
}

export interface CrisisLiveAction {
  readonly when: string;
  readonly text: string;
  readonly tags: readonly string[];
}

export function buildCrisisLiveActions(dashboard: SentinelDashboard): CrisisLiveAction[] {
  return liveRecommendationActions(dashboard);
}

/** Táticas ao vivo agrupadas por seção (surface_id). Chaves não-seção (ex.: bridge) são ignoradas no consumo. */
export type TacticsBySection = Record<string, readonly ViewTacticCard[]>;

type ActionRecommendationRow = SentinelDashboard['actionRecommendations'][number];

const TACTIC_PRIORITIES: readonly TacticPriority[] = ['alta', 'media', 'baixa'];

/** Narrowing de prioridade do payload (Record<string,unknown>); fora do enum vira 'media'. */
function asTacticPriority(value: unknown): TacticPriority {
  return TACTIC_PRIORITIES.includes(value as TacticPriority) ? (value as TacticPriority) : 'media';
}

/** Texto não-vazio do payload ou o fallback (o backend já coage, mas o tipo aqui é unknown). */
function asTacticText(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

/**
 * Converte uma linha gold.ai_action_recommendation (action_type='tactical') no card ViewTacticCard.
 * Contrato AI-CONTRACT.md#copiloto.sidebar-tactics: title→desc, confidence→confidence,
 * recommended_actions[0].impact→impact (category/priority também vêm de recommended_actions[0]).
 */
function toTacticCard(row: ActionRecommendationRow): ViewTacticCard {
  const action = row.recommended_actions[0] ?? {};
  return {
    priority: asTacticPriority(action.priority),
    category: asTacticText(action.category, 'Tática'),
    confidence: row.confidence,
    desc: row.title,
    impact: asTacticText(action.impact, '—'),
    cta: 'Ativar tática',
  };
}

/**
 * Agrupa as recomendações táticas (action_type='tactical') por seção (surface_id) p/ a sidebar do
 * Copiloto consumir ao vivo. Linhas não-táticas (bridge de alertas) são ignoradas; sem táticas → {}.
 */
export function buildTacticsBySection(dashboard: SentinelDashboard): TacticsBySection {
  const bySection: Record<string, ViewTacticCard[]> = {};
  for (const row of dashboard.actionRecommendations) {
    if (row.action_type !== 'tactical') continue;
    (bySection[row.surface_id] ??= []).push(toTacticCard(row));
  }
  return bySection;
}

/** Confiança numérica do payload (Record<string,unknown>); ausente/não-número → 0. */
function asTacticConfidence(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

/**
 * Os cards "Prioritárias da campanha" (cross-seção) — vêm da ÚNICA linha action_type='campaign-priority',
 * cujas 3 prioridades ficam em recommended_actions. Sem essa linha → [] (sidebar mantém o mock estático).
 */
export function buildCampaignPriorities(dashboard: SentinelDashboard): readonly ViewTacticCard[] {
  const row = dashboard.actionRecommendations.find((r) => r.action_type === 'campaign-priority');
  if (!row) return [];
  return row.recommended_actions.map((raw): ViewTacticCard => {
    const item = raw as Record<string, unknown>;
    return {
      priority: asTacticPriority(item.priority),
      category: asTacticText(item.category, 'Tática'),
      confidence: asTacticConfidence(item.confidence),
      desc: asTacticText(item.title, '—'),
      impact: asTacticText(item.impact, '—'),
      cta: 'Ativar tática',
    };
  });
}
